<script setup lang="ts">
import { computed, type CSSProperties, inject, ref, useId, useTemplateRef } from 'vue';
import { splitterInjectionKey } from './internal';

const props = withDefaults(
  defineProps<{
    /**
     * Smallest main-axis size (px) a drag or the keyboard leaves this panel —
     * and the group keeps when it shrinks. A floor of 32 px or more also stops a
     * drag from snapping the panel shut; its collapse button still closes it.
     */
    minSize?: number;
    /** Largest main-axis size (px) a drag, the keyboard or a growing group gives this panel. */
    maxSize?: number;
  }>(),
  { minSize: undefined, maxSize: undefined }
);

const isShown = ref<boolean>();
const heightOverwrite = ref<number>();

const panelElement = useTemplateRef('splitter-panel');

const splitterInjection = inject(splitterInjectionKey)!;

// The separator points at the first panel with `aria-controls`.
const id = useId();

const panelStyle = computed<CSSProperties | undefined>(() => {
  const size = heightOverwrite.value;
  if (!size) return;
  return splitterInjection.isVertical
    ? { maxWidth: `${size}px`, minWidth: `${size}px` }
    : { maxHeight: `${size}px`, minHeight: `${size}px` };
});

splitterInjection.addPanel({
  isShown,
  heightOverwrite,
  panelElement,
  minSize: () => props.minSize,
  maxSize: () => props.maxSize,
});
</script>

<template>
  <div v-show="isShown" :id="id" ref="splitter-panel" class="splitter-panel" :style="panelStyle">
    <slot />
  </div>
</template>

<style scoped>
.splitter-panel {
  overflow: auto;
  flex-grow: 1;
  flex-basis: 0%;
}
</style>
